import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import WbSunny from '@mui/icons-material/WbSunny';
import Cloud from '@mui/icons-material/Cloud';
import WaterDrop from '@mui/icons-material/WaterDrop';
import AcUnit from '@mui/icons-material/AcUnit';
import Thunderstorm from '@mui/icons-material/Thunderstorm';
import Star from '@mui/icons-material/Star';
import { Button, Paper } from '@mui/material';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import { useLocalization } from '@ui-framework/index';

import {
  TIME_SLOT_KEYS,
  TIME_SLOT_LABELS,
  dayHasAnySlot,
  dayHasProvidersInSegment,
  getHorizonEnd,
  getProvidersForSlot,
  listDatesInRange,
} from './availability';
import {
  SAMPLE_CALENDAR_LOCATION,
  sampleDispatchCalendarServiceProviders,
} from './sampleData';
import DispatchCalendarModal from './DispatchCalendarModal';

/** Open-Meteo forecast endpoint returns at most ~16 days from the model run date. */
const OPEN_METEO_FORECAST_DAY_COUNT = 16;

const SLOT_CHIP_PALETTE = {
  morning: { bg: '#BBDEFB', fg: '#0D47A1', hover: '#90CAF9' },
  afternoon: { bg: '#FFE0B2', fg: '#BF360C', hover: '#FFCC80' },
  evening: { bg: '#E1BEE7', fg: '#4A148C', hover: '#CE93D8' },
  allDay: { bg: '#B2DFDB', fg: '#004D40', hover: '#80CBC4' },
};

function slotChipSx(slotKey) {
  const c = SLOT_CHIP_PALETTE[slotKey] || SLOT_CHIP_PALETTE.morning;
  return {
    bgcolor: c.bg,
    color: c.fg,
    '&:hover': { bgcolor: c.hover },
  };
}

function getForecastEndInclusive(todayStart) {
  return todayStart.add(OPEN_METEO_FORECAST_DAY_COUNT - 1, 'day');
}

/** Matches DayCell: first bookable day is tomorrow; optional time-slot filter. */
function dayHasVisibleBookableSlot(
  serviceProviders,
  date,
  tomorrow,
  horizonEnd,
  timeSlotFilters,
) {
  if (date.isBefore(tomorrow, 'day') || date.isAfter(horizonEnd, 'day')) {
    return false;
  }
  return TIME_SLOT_KEYS.some(slotKey => {
    if (timeSlotFilters.length > 0 && !timeSlotFilters.includes(slotKey)) {
      return false;
    }
    return getProvidersForSlot(serviceProviders, date, slotKey).length > 0;
  });
}

function weatherIcon(code) {
  if (code == null) return null;
  const size = { fontSize: 22 };
  if ([0, 1].includes(code)) {
    return (
      <WbSunny
        sx={{
          ...size,
          color: '#F9A825',
          filter: 'drop-shadow(0 0 2px rgba(249, 168, 37, 0.45))',
        }}
      />
    );
  }
  if ([2].includes(code)) {
    return <Cloud sx={{ ...size, color: '#64B5F6' }} />;
  }
  if ([3].includes(code)) {
    return <Cloud sx={{ ...size, color: '#78909C' }} />;
  }
  if ([45, 48].includes(code)) {
    return <Cloud sx={{ ...size, color: '#B0BEC5' }} />;
  }
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) {
    return <WaterDrop sx={{ ...size, color: '#1E88E5' }} />;
  }
  if ([71, 73, 75, 77, 85, 86].includes(code)) {
    return <AcUnit sx={{ ...size, color: '#4FC3F7' }} />;
  }
  if ([95, 96, 99].includes(code)) {
    return <Thunderstorm sx={{ ...size, color: '#5E35B1' }} />;
  }
  return <Cloud sx={{ ...size, color: '#78909C' }} />;
}

/** Short label for Open-Meteo WMO weathercode (daily). */
function describeWmoWeather(code) {
  if (code == null) return null;
  if (code === 0) return 'Clear sky';
  if (code === 1) return 'Mainly clear';
  if (code === 2) return 'Partly cloudy';
  if (code === 3) return 'Overcast';
  if ([45, 48].includes(code)) return 'Fog';
  if ([51, 53, 55].includes(code)) return 'Drizzle';
  if ([56, 57].includes(code)) return 'Freezing drizzle';
  if ([61, 63, 65].includes(code)) return 'Rain';
  if ([66, 67].includes(code)) return 'Freezing rain';
  if ([71, 73, 75, 77].includes(code)) return 'Snow';
  if ([80, 81, 82].includes(code)) return 'Rain showers';
  if ([85, 86].includes(code)) return 'Snow showers';
  if (code === 95) return 'Thunderstorm';
  if ([96, 99].includes(code)) return 'Thunderstorm with hail';
  return 'Mixed conditions';
}

function buildWeatherDayTooltip(weather) {
  if (!weather) {
    return 'No forecast available for this day.';
  }
  const condition = describeWmoWeather(weather.code);
  const tempOk = weather.maxTemp != null && !Number.isNaN(weather.maxTemp);
  const high = tempOk ? Math.round(weather.maxTemp) : null;

  if (condition && high != null) {
    return `${condition}. Forecast high ${high}°C.`;
  }
  if (condition) {
    return `${condition}.`;
  }
  if (high != null) {
    return `Forecast high ${high}°C.`;
  }
  return 'No forecast available for this day.';
}

function WeatherCorner({ weather }) {
  const hasData =
    weather &&
    (weather.code != null ||
      (weather.maxTemp != null && !Number.isNaN(weather.maxTemp)));
  const tooltipTitle = hasData
    ? buildWeatherDayTooltip(weather)
    : 'No forecast available for this day.';

  return (
    <Tooltip describeChild title={tooltipTitle} placement="top">
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          minWidth: 40,
          minHeight: 48,
          justifyContent: 'flex-start',
          cursor: 'help',
        }}>
        {hasData ? (
          <Stack alignItems="center" spacing={0.25}>
            {weather.code != null ? (
              weatherIcon(weather.code)
            ) : (
              <Box aria-hidden sx={{ width: 22, height: 22 }} />
            )}
            {weather.maxTemp != null && !Number.isNaN(weather.maxTemp) ? (
              <Typography
                variant="caption"
                lineHeight={1.1}
                color="text.secondary">
                {`${Math.round(weather.maxTemp)}°`}
              </Typography>
            ) : (
              <Box aria-hidden sx={{ minHeight: 16, width: 28 }} />
            )}
          </Stack>
        ) : (
          <>
            <Box aria-hidden sx={{ width: 22, height: 22 }} />
            <Box aria-hidden sx={{ mt: 0.25, minHeight: 16, width: 28 }} />
          </>
        )}
      </Box>
    </Tooltip>
  );
}

function formatViewTitle(view, referenceDate) {
  if (view === 'month') {
    return referenceDate.format('MMMM YYYY');
  }
  const weekEnd = referenceDate.add(6, 'day');
  return `${referenceDate.format('MMM D')} – ${weekEnd.format('MMM D, YYYY')}`;
}

/** Week view only: show N-days-from-today hint when farther than this (calendar days). */
const WEEK_VIEW_DAY_OFFSET_HINT_THRESHOLD = 6;

function getWeekRelativeDayHintLabel(
  enabled,
  dayOffsetFromToday,
  threshold = WEEK_VIEW_DAY_OFFSET_HINT_THRESHOLD,
) {
  if (!enabled) return null;
  const absOffset = Math.abs(dayOffsetFromToday);
  if (absOffset <= threshold) return null;
  if (dayOffsetFromToday > 0) return `In ${absOffset} days`;
  return `${absOffset} days ago`;
}

async function fetchOpenMeteoDaily(lat, lng, startStr, endStr) {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    daily: 'weathercode,temperature_2m_max',
    start_date: startStr,
    end_date: endStr,
    timezone: 'America/New_York',
  });
  const res = await fetch(
    `https://api.open-meteo.com/v1/forecast?${params.toString()}`,
  );
  if (!res.ok) throw new Error('weather');
  const json = await res.json();
  const dates = json?.daily?.time || [];
  const codes = json?.daily?.weathercode || [];
  const temps = json?.daily?.temperature_2m_max || [];
  const map = {};
  dates.forEach((d, i) => {
    map[d] = {
      code: codes[i],
      maxTemp: temps[i],
    };
  });
  return map;
}

function useWeather(lat, lng, startStr, endStr) {
  const [byDate, setByDate] = useState({});

  useEffect(() => {
    if (!startStr || !endStr) {
      setByDate({});
      return undefined;
    }
    let cancelled = false;
    setByDate({});
    fetchOpenMeteoDaily(lat, lng, startStr, endStr)
      .then(data => {
        if (!cancelled) setByDate(data);
      })
      .catch(() => {
        if (!cancelled) setByDate({});
      });
    return () => {
      cancelled = true;
    };
  }, [lat, lng, startStr, endStr]);

  return { byDate };
}

function DayCell({
  date,
  today,
  horizonEnd,
  serviceProviders,
  weatherByDate,
  timeSlotFilters,
  onOpenSlot,
  /** When true (week view), show small hint if the cell date is more than six days from today. */
  showWeekRelativeDayHint = false,
}) {
  const { formatMessage } = useLocalization();
  const dateStr = date.format('YYYY-MM-DD');
  const isBeforeToday = date.isBefore(today, 'day');
  const isAfterHorizon = date.isAfter(horizonEnd, 'day');
  const disabled = isBeforeToday || isAfterHorizon;
  /** Slots and weather only for days strictly after today (within horizon). */
  const hasDataLayer =
    date.isAfter(today, 'day') && !date.isAfter(horizonEnd, 'day');

  const slotsToRender = hasDataLayer
    ? TIME_SLOT_KEYS.filter(slotKey => {
        if (timeSlotFilters.length > 0 && !timeSlotFilters.includes(slotKey)) {
          return false;
        }
        const list = getProvidersForSlot(serviceProviders, date, slotKey);
        return list.length > 0;
      })
    : [];

  const weather = weatherByDate[dateStr];
  const dimmed = disabled || !hasDataLayer;
  const dayOffsetFromToday = date
    .startOf('day')
    .diff(today.startOf('day'), 'day');
  const weekRelativeDayHint = getWeekRelativeDayHintLabel(
    showWeekRelativeDayHint,
    dayOffsetFromToday,
  );
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: 120,
        p: 1,
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        bgcolor: dimmed ? 'action.hover' : 'background.paper',
        opacity: dimmed ? 0.55 : 1,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={0.5}
        sx={{ width: '100%' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ minWidth: 0 }}>
          {date.format('ddd D')}
        </Typography>
        {hasDataLayer && (
          <Box sx={{ flexShrink: 0, mt: -0.25, mr: -0.25 }}>
            <WeatherCorner weather={weather} />
          </Box>
        )}
      </Stack>

      {hasDataLayer && (
        <Stack
          direction="column"
          alignItems="stretch"
          spacing={0.5}
          sx={{ mt: 1, flex: 1 }}>
          {slotsToRender.map(slotKey => {
            const list = getProvidersForSlot(serviceProviders, date, slotKey);
            const assignedSp = list[0];
            const isPreferred = Boolean(assignedSp?.preferredSP);
            return (
              <Chip
                key={slotKey}
                size="small"
                label={
                  <Stack
                    direction="row"
                    alignItems="center"
                    component="span"
                    spacing={0.75}
                    sx={{ width: '100%', minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                      {TIME_SLOT_LABELS[slotKey]}
                    </Box>
                    {isPreferred ? (
                      <Tooltip
                        title={formatMessage({
                          id: 'DISPATCH_PREFERRED_SP_TOOLTIP',
                          defaultMessage: 'Recommended',
                        })}
                        placement="top">
                        <Star
                          sx={{
                            fontSize: 18,
                            flexShrink: 0,
                            color: '#FACC15',
                            stroke: '#000000',
                            strokeWidth: 2,
                            paintOrder: 'stroke fill',
                          }}
                        />
                      </Tooltip>
                    ) : null}
                  </Stack>
                }
                variant="filled"
                onClick={() =>
                  onOpenSlot({
                    dateStr,
                    slotKey,
                    label: `${TIME_SLOT_LABELS[slotKey]} — ${date.format('MMM D, YYYY')}`,
                    providers: list,
                  })
                }
                sx={{
                  cursor: 'pointer',
                  width: '100%',
                  justifyContent: 'flex-start',
                  border: 'none',
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0.25,
                    width: '100%',
                    display: 'block',
                  },
                  ...slotChipSx(slotKey),
                }}
              />
            );
          })}
        </Stack>
      )}
      {weekRelativeDayHint != null && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            mt: 1.5,
            lineHeight: 1,
            display: 'block',
            textAlign: 'right',
            fontWeight: '500',
          }}>
          {weekRelativeDayHint}
        </Typography>
      )}
    </Box>
  );
}

const VIEW_OPTIONS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

export default function Calendar({
  serviceProviders = sampleDispatchCalendarServiceProviders,
  maxDays = 30,
  weatherLatitude = SAMPLE_CALENDAR_LOCATION.latitude,
  weatherLongitude = SAMPLE_CALENDAR_LOCATION.longitude,
  /** When true, shows summary stat blocks above the calendar toolbar. */
  showStats = false,
  serviceJob,
}) {
  const today = useMemo(() => dayjs().startOf('day'), []);
  const tomorrow = useMemo(() => today.add(1, 'day').startOf('day'), [today]);
  const horizonEnd = useMemo(
    () => getHorizonEnd(today, maxDays),
    [today, maxDays],
  );

  const [view, setView] = useState('month');
  const [referenceDate, setReferenceDate] = useState(() =>
    dayjs().startOf('day'),
  );
  const [timeSlotFilters, setTimeSlotFilters] = useState([]);
  const [serviceProviderFilters, setServiceProviderFilters] = useState([]);
  const [modal, setModal] = useState(null);

  const filteredServiceProviders = useMemo(() => {
    if (!Array.isArray(serviceProviders)) return [];
    if (serviceProviderFilters.length === 0) return serviceProviders;
    const allowed = new Set(serviceProviderFilters);
    return serviceProviders.filter(sp => allowed.has(sp.id));
  }, [serviceProviders, serviceProviderFilters]);

  const firstAvailableDate = useMemo(() => {
    let cursor = tomorrow;
    while (!cursor.isAfter(horizonEnd, 'day')) {
      if (
        dayHasVisibleBookableSlot(
          filteredServiceProviders,
          cursor,
          tomorrow,
          horizonEnd,
          timeSlotFilters,
        )
      ) {
        return cursor.startOf('day');
      }
      cursor = cursor.add(1, 'day');
    }
    return null;
  }, [tomorrow, horizonEnd, filteredServiceProviders, timeSlotFilters]);

  /** Earliest day a week strip may start (first visible slot, else tomorrow). */
  const weekViewStartMin = useMemo(
    () => (firstAvailableDate ?? tomorrow).startOf('day'),
    [firstAvailableDate, tomorrow],
  );

  useEffect(() => {
    if (view !== 'week') return undefined;
    setReferenceDate(rd => {
      const cur = rd.startOf('day');
      let next = cur;
      if (cur.isBefore(weekViewStartMin, 'day')) {
        next = weekViewStartMin;
      }
      const windowEnd = next.add(6, 'day');
      if (windowEnd.isAfter(horizonEnd, 'day')) {
        const pulled = horizonEnd.subtract(6, 'day').startOf('day');
        next = pulled.isBefore(weekViewStartMin, 'day')
          ? weekViewStartMin
          : pulled;
      }
      return next.isSame(cur, 'day') ? cur : next;
    });
    return undefined;
  }, [view, weekViewStartMin, horizonEnd]);

  /** Dates rendered in the current view (week / month), used for weather and stats. */
  const weatherSurfaceDates = useMemo(() => {
    if (view === 'week') {
      const start = referenceDate.startOf('day');
      return listDatesInRange(start, start.add(6, 'day'));
    }
    if (view === 'month') {
      const monthStart = referenceDate.startOf('month');
      const dim = monthStart.daysInMonth();
      const dates = [];
      for (let i = 0; i < dim; i += 1) {
        dates.push(monthStart.add(i, 'day'));
      }
      return dates;
    }
    return [];
  }, [view, referenceDate]);

  /** Bookable days in the current view within the dispatch horizon (for stats). */
  const statsEligibleDates = useMemo(
    () =>
      weatherSurfaceDates.filter(
        d => d.isAfter(today, 'day') && !d.isAfter(horizonEnd, 'day'),
      ),
    [weatherSurfaceDates, today, horizonEnd],
  );

  const weatherRange = useMemo(() => {
    if (!weatherSurfaceDates.length) return { startStr: '', endStr: '' };
    let minD = weatherSurfaceDates[0];
    let maxD = weatherSurfaceDates[0];
    weatherSurfaceDates.forEach(d => {
      if (d.isBefore(minD, 'day')) minD = d;
      if (d.isAfter(maxD, 'day')) maxD = d;
    });
    const forecastEnd = getForecastEndInclusive(today);
    const firstDataDay = today.add(1, 'day');
    const start = minD.isBefore(firstDataDay, 'day') ? firstDataDay : minD;
    let end = maxD.isAfter(horizonEnd, 'day') ? horizonEnd : maxD;
    if (end.isAfter(forecastEnd, 'day')) {
      end = forecastEnd;
    }
    if (start.isAfter(horizonEnd, 'day') || start.isAfter(end, 'day')) {
      return { startStr: '', endStr: '' };
    }
    return {
      startStr: start.format('YYYY-MM-DD'),
      endStr: end.format('YYYY-MM-DD'),
    };
  }, [weatherSurfaceDates, horizonEnd, today]);

  const { byDate: weatherByDate } = useWeather(
    weatherLatitude,
    weatherLongitude,
    weatherRange.startStr,
    weatherRange.endStr,
  );

  const visibleDates = useMemo(() => {
    if (view === 'week') {
      const start = referenceDate.startOf('day');
      return listDatesInRange(start, start.add(6, 'day'));
    }
    return [];
  }, [view, referenceDate]);

  const stats = useMemo(() => {
    if (!showStats) {
      return {
        totalSp: 0,
        daysMorning: 0,
        daysAfternoon: 0,
        daysEvening: 0,
        emptyDays: 0,
      };
    }

    const spInView = new Set();
    let daysMorning = 0;
    let daysAfternoon = 0;
    let daysEvening = 0;
    let emptyDays = 0;

    statsEligibleDates.forEach(d => {
      TIME_SLOT_KEYS.forEach(slotKey => {
        getProvidersForSlot(filteredServiceProviders, d, slotKey).forEach(sp =>
          spInView.add(sp.id),
        );
      });
      if (!dayHasAnySlot(filteredServiceProviders, d)) {
        emptyDays += 1;
      } else {
        if (dayHasProvidersInSegment(filteredServiceProviders, d, 'morning')) {
          daysMorning += 1;
        }
        if (
          dayHasProvidersInSegment(filteredServiceProviders, d, 'afternoon')
        ) {
          daysAfternoon += 1;
        }
        if (dayHasProvidersInSegment(filteredServiceProviders, d, 'evening')) {
          daysEvening += 1;
        }
      }
    });

    return {
      totalSp: spInView.size,
      daysMorning,
      daysAfternoon,
      daysEvening,
      emptyDays,
    };
  }, [showStats, filteredServiceProviders, statsEligibleDates]);

  const canGoPrev = useMemo(() => {
    if (view === 'week') {
      return referenceDate.startOf('day').isAfter(weekViewStartMin, 'day');
    }
    const prevMonthEnd = referenceDate
      .startOf('month')
      .subtract(1, 'month')
      .endOf('month');
    return (
      prevMonthEnd.isAfter(today, 'day') || prevMonthEnd.isSame(today, 'day')
    );
  }, [view, referenceDate, weekViewStartMin, today]);

  const canGoNext = useMemo(() => {
    if (view === 'week') {
      const nextWindowEnd = referenceDate.startOf('day').add(13, 'day');
      return !nextWindowEnd.isAfter(horizonEnd, 'day');
    }
    const nextMonthStart = referenceDate.startOf('month').add(1, 'month');
    return !nextMonthStart.isAfter(horizonEnd, 'day');
  }, [view, referenceDate, horizonEnd]);

  const goPrev = useCallback(() => {
    if (view === 'week') {
      setReferenceDate(d => {
        const next = d.startOf('day').subtract(7, 'day');
        return next.isBefore(weekViewStartMin, 'day') ? weekViewStartMin : next;
      });
      return;
    }
    setReferenceDate(d => d.subtract(1, 'month').startOf('month'));
  }, [view, weekViewStartMin]);

  const goNext = useCallback(() => {
    if (view === 'week') {
      setReferenceDate(d => {
        const next = d.startOf('day').add(7, 'day');
        const nextWindowEnd = next.add(6, 'day');
        if (nextWindowEnd.isAfter(horizonEnd, 'day')) {
          return d.startOf('day');
        }
        return next;
      });
      return;
    }
    setReferenceDate(d => d.add(1, 'month').startOf('month'));
  }, [view, horizonEnd]);

  const monthGrid = useMemo(() => {
    if (view !== 'month') return null;
    const monthStart = referenceDate.startOf('month');
    const pad = monthStart.day();
    const cells = [];
    for (let i = 0; i < pad; i += 1) cells.push(null);
    const dim = monthStart.daysInMonth();
    for (let d = 0; d < dim; d += 1) {
      cells.push(monthStart.add(d, 'day'));
    }
    while (cells.length % 7 !== 0) cells.push(null);

    const resolveCalendarDate = cellIndex => {
      const raw = cells[cellIndex];
      if (raw) return raw.startOf('day');
      if (cellIndex < pad) {
        return monthStart.subtract(pad - cellIndex, 'day').startOf('day');
      }
      return monthStart
        .add(dim - 1 + (cellIndex - (pad + dim - 1)), 'day')
        .startOf('day');
    };

    const kept = [];
    for (let rowStart = 0; rowStart < cells.length; rowStart += 7) {
      let hasBookableDayInRow = false;
      let rowHasVisibleBookableSlot = false;
      let anyBookableOnOrAfterFirst = false;
      let rowLatestDate = null;
      for (let c = 0; c < 7; c += 1) {
        const d = resolveCalendarDate(rowStart + c);
        if (!rowLatestDate || d.isAfter(rowLatestDate, 'day')) {
          rowLatestDate = d;
        }
        const inBookableRange =
          !d.isBefore(tomorrow, 'day') && !d.isAfter(horizonEnd, 'day');
        if (inBookableRange) {
          hasBookableDayInRow = true;
          if (firstAvailableDate && !d.isBefore(firstAvailableDate, 'day')) {
            anyBookableOnOrAfterFirst = true;
          }
        }
        if (
          dayHasVisibleBookableSlot(
            filteredServiceProviders,
            d,
            tomorrow,
            horizonEnd,
            timeSlotFilters,
          )
        ) {
          rowHasVisibleBookableSlot = true;
        }
      }
      const hidePastWeekRow = rowLatestDate.isBefore(tomorrow, 'day');
      const hideEmptyBeforeFirstRow =
        hasBookableDayInRow &&
        !rowHasVisibleBookableSlot &&
        (firstAvailableDate ? !anyBookableOnOrAfterFirst : true);
      const hideRow = hidePastWeekRow || hideEmptyBeforeFirstRow;
      if (!hideRow) {
        for (let k = 0; k < 7; k += 1) {
          kept.push(cells[rowStart + k]);
        }
      }
    }

    return { cells: kept, monthStart };
  }, [
    view,
    referenceDate,
    filteredServiceProviders,
    tomorrow,
    horizonEnd,
    firstAvailableDate,
    timeSlotFilters,
  ]);

  const onOpenSlot = useCallback(payload => {
    setModal(payload);
  }, []);

  const statBlock = (label, value) => (
    <Box
      key={label}
      sx={{
        flex: '1 1 160px',
        p: 2,
        borderRadius: 1,
        border: t => `1px solid ${t.palette.divider}`,
        bgcolor: 'background.default',
      }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {showStats && (
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={2}
          sx={{ mb: 2 }}>
          {statBlock('SP in this view', stats.totalSp)}
          {statBlock('Days with SP (morning)', stats.daysMorning)}
          {statBlock('Days with SP (afternoon)', stats.daysAfternoon)}
          {statBlock('Days with SP (evening)', stats.daysEvening)}
          {statBlock('Days with no slots', stats.emptyDays)}
        </Stack>
      )}
      <Paper sx={{ p: 1, mb: 2 }} elevation={0}>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={1}
          sx={{ mb: 2, width: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            spacing={1}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={(_, v) => {
                if (!v) return;
                setView(v);
                if (v === 'week') {
                  setReferenceDate(weekViewStartMin);
                }
              }}>
              {VIEW_OPTIONS.map(opt => (
                <ToggleButton key={opt.value} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                aria-label="Previous"
                onClick={goPrev}
                disabled={!canGoPrev}
                size="small">
                <ChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{ minWidth: 160, textAlign: 'center' }}>
                {formatViewTitle(view, referenceDate)}
              </Typography>
              <IconButton
                aria-label="Next"
                onClick={goNext}
                disabled={!canGoNext}
                size="small">
                <ChevronRight />
              </IconButton>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            spacing={2}
            sx={{ ml: 'auto' }}>
            <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
              <InputLabel id="dispatch-cal-sp-filter" shrink>
                &nbsp;
              </InputLabel>
              <Button
                startIcon={<ScheduleSendIcon />}
                onClick={() => {
                  setModal({ slotKey: null });
                }}
                variant="contained">
                Request Alternate Time
              </Button>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
              <InputLabel id="dispatch-cal-sp-filter" shrink>
                Service provider
              </InputLabel>
              <Select
                labelId="dispatch-cal-sp-filter"
                id="dispatch-cal-sp-filter-select"
                multiple
                displayEmpty
                value={serviceProviderFilters}
                onChange={e => {
                  const v = e.target.value;
                  setServiceProviderFilters(
                    typeof v === 'string'
                      ? v.split(',').filter(Boolean)
                      : [...v],
                  );
                }}
                input={<OutlinedInput notched label="Service provider" />}
                renderValue={selected => {
                  if (selected.length === 0) return 'All providers';
                  return selected
                    .map(
                      id =>
                        serviceProviders.find(sp => sp.id === id)?.name || id,
                    )
                    .join(', ');
                }}
                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}>
                {(Array.isArray(serviceProviders) ? serviceProviders : []).map(
                  sp => (
                    <MenuItem
                      key={sp.id}
                      value={sp.id}
                      dense
                      sx={{ gap: 1, py: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={serviceProviderFilters.includes(sp.id)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{
                          'aria-labelledby': `dispatch-sp-${sp.id}`,
                        }}
                      />
                      <Typography
                        id={`dispatch-sp-${sp.id}`}
                        variant="body2"
                        sx={{ minWidth: 0 }}>
                        {sp.name}
                      </Typography>
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200, maxWidth: 280 }}>
              <InputLabel id="dispatch-cal-slot-filter" shrink>
                Time slot
              </InputLabel>
              <Select
                labelId="dispatch-cal-slot-filter"
                id="dispatch-cal-slot-filter-select"
                multiple
                displayEmpty
                value={timeSlotFilters}
                onChange={e => {
                  const v = e.target.value;
                  setTimeSlotFilters(
                    typeof v === 'string'
                      ? v.split(',').filter(Boolean)
                      : [...v],
                  );
                }}
                input={<OutlinedInput notched label="Time slot" />}
                renderValue={selected =>
                  selected.length === 0
                    ? 'All slots'
                    : selected.map(k => TIME_SLOT_LABELS[k]).join(', ')
                }
                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}>
                {TIME_SLOT_KEYS.map(k => (
                  <MenuItem key={k} value={k} dense sx={{ gap: 1, py: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={timeSlotFilters.includes(k)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': `dispatch-slot-${k}` }}
                    />
                    <Typography id={`dispatch-slot-${k}`} variant="body2">
                      {TIME_SLOT_LABELS[k]}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Paper>
      <Paper sx={{ p: 1, mb: 3 }} elevation={0}>
        {view === 'month' && monthGrid && (
          <Box sx={{ width: '100%' }}>
            <Stack direction="row" sx={{ mb: 0.5 }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(h => (
                <Box key={h} sx={{ flex: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {h}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: 0.5,
              }}>
              {monthGrid.cells.map((cell, idx) => (
                <Box
                  key={
                    cell
                      ? cell.format('YYYY-MM-DD')
                      : `pad-${monthGrid.monthStart.format('YYYY-MM')}-${idx}`
                  }
                  sx={{ minHeight: 130 }}>
                  {cell ? (
                    <DayCell
                      date={cell}
                      today={today}
                      horizonEnd={horizonEnd}
                      serviceProviders={filteredServiceProviders}
                      weatherByDate={weatherByDate}
                      timeSlotFilters={timeSlotFilters}
                      onOpenSlot={onOpenSlot}
                    />
                  ) : (
                    <Box />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {view === 'week' && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(7, minmax(0, 1fr))',
              },
              gap: 1,
              width: '100%',
            }}>
            {visibleDates.map(d => (
              <DayCell
                key={d.format('YYYY-MM-DD')}
                date={d}
                today={today}
                horizonEnd={horizonEnd}
                serviceProviders={filteredServiceProviders}
                weatherByDate={weatherByDate}
                timeSlotFilters={timeSlotFilters}
                onOpenSlot={onOpenSlot}
                showWeekRelativeDayHint
              />
            ))}
          </Box>
        )}
      </Paper>

      {serviceJob?.serviceJobNumber && modal && (
        <DispatchCalendarModal
          open={Boolean(modal)}
          onClose={() => setModal(null)}
          title={modal?.label || ''}
          providers={modal?.providers || []}
          selectedSlot={modal}
          jobInfo={serviceJob}
        />
      )}
    </Box>
  );
}
